import { LoadingButton } from '@mui/lab'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, SxProps, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { CustomersClient } from '../../interactors/clients/CustomerClient'
import { backendClient } from '../../interactors/clients/client'
import { useAccountsStore } from '../../store/AccountsStore'

interface Props {
  variant?: 'text' | 'outlined' | 'contained'
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  sx?: SxProps
  content?: string
  fullWidth?: boolean
  onClick?: () => void
  onConnectAttempt?: () => void
}

export const StripeConnectButton: React.FC<Props> = ({
  variant,
  color,
  sx,
  content,
  fullWidth = true,
  onClick,
  onConnectAttempt,
}) => {
  const [loading, setLoading] = useState(false)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [emailSaving, setEmailSaving] = useState(false)

  const accountsStore = useAccountsStore()

  const runStripeConnect = async () => {
    setLoading(true)
    try {
      const resLink = await backendClient.get('/customers/stripe-connect-link')
      const link = resLink.data

      // Open a new tab for the user to the Stripe Connect onboarding link.
      window.open(link, '_blank')
    } catch (error) {
      console.error('Error connecting to Stripe:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async () => {
    if (!accountsStore.isAdmin()) return
    if (onClick) {
      onClick()
      return
    }
    onConnectAttempt?.()
    const currentCustomer = accountsStore.connectedCustomer
    if (!currentCustomer?.email) {
      setEmail(currentCustomer?.email || '')
      setEmailError('')
      setEmailModalOpen(true)
      return
    }

    await runStripeConnect()
  }

  const handleSaveEmail = async () => {
    const trimmed = email.trim()
    if (!trimmed || !/\S+@\S+\.\S+/.test(trimmed)) {
      setEmailError("L'adresse email n'est pas valide.")
      return
    }

    const currentCustomer =
      accountsStore.connectedCustomer ?? (await CustomersClient.getCurrentCustomer().catch(() => null))
    if (!currentCustomer) {
      setEmailError("Impossible de récupérer l'association.")
      return
    }

    setEmailSaving(true)
    setEmailError('')
    try {
      const updatedCustomer = await CustomersClient.editCustomer({
        ...currentCustomer,
        email: trimmed,
      })
      accountsStore.changeConnectedCustomer(updatedCustomer)
      setEmailModalOpen(false)
      await runStripeConnect()
    } catch (error) {
      setEmailError("Une erreur est survenue lors de l'enregistrement.")
    } finally {
      setEmailSaving(false)
    }
  }

  return (
    <>
      <LoadingButton
        variant={variant ?? 'contained'}
        color={color ?? 'secondary'}
        fullWidth={fullWidth}
        onClick={handleConnect}
        disabled={loading || !accountsStore.isAdmin()}
        loading={loading}
        sx={sx}
      >
        {content ?? `Configurer mon Stripe Connect`}
      </LoadingButton>

      <Dialog open={emailModalOpen} onClose={() => setEmailModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adresse email de l&apos;association</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Nous avons besoin d&apos;une adresse email pour cr&eacute;er votre compte Stripe Connect.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Email de l'association"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              if (emailError) {
                setEmailError('')
              }
            }}
            error={Boolean(emailError)}
            helperText={emailError}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
            <LoadingButton variant="outlined" onClick={() => setEmailModalOpen(false)}>
              Annuler
            </LoadingButton>
            <LoadingButton variant="contained" loading={emailSaving} onClick={handleSaveEmail}>
              Enregistrer et continuer
            </LoadingButton>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}
